import React, { useState } from "react";
import { Modal, Form, Input, Button, Space, Typography, Divider } from "antd";
import {
  LinkOutlined,
  UserOutlined,
  SafetyCertificateOutlined,
  KeyOutlined,
  LockOutlined,
} from "@ant-design/icons";
import { postApi } from "redux/apis";
import { toastText } from "utils/utils";
import styles from "./index.module.scss";
import { useNavigate } from "react-router-dom";

const { Title, Text } = Typography;

const NetSuiteAuthModal = (props: any) => {
  console.log("NetSuiteAuthModal props:", props);

  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const { isModalVisible, setIsModalVisible } = props;
  const [form] = Form.useForm();

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleSubmit = async (values: any) => {
    setLoading(true);
    try {
      console.log("NetSuite Auth Data:", values);

      const response = await postApi(`/netsuite/save`, values);

      if (response?.status === 200) {
        toastText("NetSuite credentials saved successfully!", "success");
        setIsModalVisible(false); // Close modal on success
        navigate("/connectedapp");
      } else {
        // Handle API error response
        // const errorMessage =
        //   response?.data?.error?.message ||
        //   "Failed to save NetSuite credentials. Please try again.";
        // toastText(errorMessage, "error");
      }
    } catch (error: any) {
      console.error("NetSuite authentication error:", error);
      // Handle network or unexpected errors
      // const errorMessage =
      //   error?.response?.data?.error?.message ||
      //   error?.message ||
      ("An unexpected error occurred. Please try again.");
      toastText("Netsuite authentication failed: ", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      title={
        <div style={{ textAlign: "center" }}>
          <SafetyCertificateOutlined
            style={{ fontSize: "24px", color: "#1890ff", marginRight: "8px" }}
          />
          <Title level={3} style={{ margin: 0, display: "inline" }}>
            NetSuite Authentication
          </Title>
        </div>
      }
      open={isModalVisible}
      onCancel={handleCancel}
      footer={null}
      width={600}
      centered
      destroyOnClose
      className={styles.netsuiteModal}
      styles={{
        body: { maxHeight: "calc(92vh - 90px)", overflowY: "auto" },
      }}
    >
      <div className={styles.modalContent}>
        <Text className={styles.description}>
          Enter your NetSuite credentials to establish a secure connection
        </Text>

        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          requiredMark={false}
          className={styles.authForm}
        >
          <div className={styles.formSection}>
            <Form.Item
              label="Account ID"
              name="accountid"
              className={styles.formItem}
              rules={[
                { required: true, message: "Please enter your Account ID" },
                { min: 3, message: "Account ID must be at least 3 characters" },
                { whitespace: true, message: "Account ID cannot be empty" },
              ]}
            >
              <Input.Password
                prefix={<UserOutlined className={styles.inputIcon} />}
                placeholder="Enter NetSuite Account ID"
                className={styles.inputField}
              />
            </Form.Item>

            <Form.Item
              label="API Base URL"
              name="apibaseurl"
              className={styles.formItem}
              rules={[
                { required: true, message: "Please enter the API Base URL" },
                { type: "url", message: "Please enter a valid URL" },
                {
                  pattern: /^https:\/\//,
                  message: "URL must use HTTPS protocol",
                },
              ]}
            >
              <Input
                prefix={<LinkOutlined className={styles.inputIcon} />}
                placeholder="https://your-account.suitetalk.api.netsuite.com"
                className={styles.inputField}
              />
            </Form.Item>
          </div>

          <Divider className={styles.sectionDivider}>
            <Text className={styles.dividerText}>OAuth Credentials</Text>
          </Divider>

          <div className={styles.formSection}>
            <div className={styles.formRow}>
              <Form.Item
                label="Consumer Key"
                name="consumerkey"
                className={styles.formItemHalf}
                rules={[
                  { required: true, message: "Please enter your Consumer Key" },
                  {
                    min: 10,
                    message: "Consumer Key must be at least 10 characters",
                  },
                  { whitespace: true, message: "Consumer Key cannot be empty" },
                ]}
              >
                <Input.Password
                  prefix={<KeyOutlined className={styles.inputIcon} />}
                  placeholder="Consumer Key"
                  className={styles.inputField}
                />
              </Form.Item>

              <Form.Item
                label="Consumer Secret"
                name="consumersecret"
                className={styles.formItemHalf}
                rules={[
                  {
                    required: true,
                    message: "Please enter your Consumer Secret",
                  },
                  {
                    whitespace: true,
                    message: "Consumer Secret cannot be empty",
                  },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined className={styles.inputIcon} />}
                  placeholder="Consumer Secret"
                  className={styles.inputField}
                />
              </Form.Item>
            </div>

            <div className={styles.formRow}>
              <Form.Item
                label="Token Key"
                name="tokenkey"
                className={styles.formItemHalf}
                rules={[
                  { required: true, message: "Please enter your Token Key" },
                  { whitespace: true, message: "Token Key cannot be empty" },
                ]}
              >
                <Input.Password
                  prefix={<KeyOutlined className={styles.inputIcon} />}
                  placeholder="Token Key"
                  className={styles.inputField}
                />
              </Form.Item>

              <Form.Item
                label="Token Secret"
                name="tokensecret"
                className={styles.formItemHalf}
                rules={[
                  { required: true, message: "Please enter your Token Secret" },
                  {
                    min: 10,
                    message: "Token Secret must be at least 10 characters",
                  },
                  { whitespace: true, message: "Token Secret cannot be empty" },
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined className={styles.inputIcon} />}
                  placeholder="Token Secret"
                  className={styles.inputField}
                />
              </Form.Item>
            </div>
          </div>

          <div className={styles.formActions}>
            <Space className={styles.buttonGroup}>
              <Button
                onClick={handleCancel}
                className={styles.cancelButton}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                className={styles.submitButton}
                loading={loading}
                disabled={loading}
              >
                Connect to NetSuite
              </Button>
            </Space>
          </div>
        </Form>
      </div>
    </Modal>
  );
};

export default NetSuiteAuthModal;
